import { basenamePath, fileUrl, isKicadPcb, isKicadPro, isKicadSch } from "./kicad-path.ts";

export { isKicadPro };

export const KICANVAS_SCRIPT_SRC = "/vendor/kicanvas/kicanvas-b031159.js";
export const KICANVAS_SELECT_EVENT = "kicanvas:select";
export const KICANVAS_ICON_FONT = "24px 'Material Symbols Outlined'";
export const KICANVAS_ICON_FONT_HREF = "/vendor/kicanvas/fonts/material-symbols-outlined.woff2";

export type KiCanvasPageRef = {
  filename?: string | null;
  project_path?: string | null;
};

export function pathFromKiCanvasPage(
  page: KiCanvasPageRef | null | undefined,
  sources: Array<{ path: string; name?: string }>,
): string | null {
  if (!page) return null;
  const tokens = [page.filename, page.project_path].flatMap((value) => {
    if (typeof value !== "string" || !value.trim()) return [];
    const trimmed = normalizeTreePath(value.trim());
    const filePart = trimmed.split(":")[0] ?? trimmed;
    return [trimmed, filePart, basenamePath(filePart)];
  });
  for (const token of tokens) {
    const hit = sources.find((src) => {
      const path = normalizeTreePath(src.path);
      const name = normalizeTreePath(src.name ?? "");
      return path === token || name === token || basenamePath(path) === basenamePath(token);
    });
    if (hit) return normalizeTreePath(hit.path);
  }
  return null;
}

type KiCanvasProjectLike = {
  active_page?: KiCanvasPageRef & { name?: string };
  set_active_page?: (token: string) => void;
  pages?: () => Iterable<KiCanvasPageRef & { project_path?: string; filename?: string }>;
  addEventListener?: (type: string, handler: EventListener) => void;
  removeEventListener?: (type: string, handler: EventListener) => void;
  on?: (type: string, handler: (...args: unknown[]) => void) => void;
  off?: (type: string, handler: (...args: unknown[]) => void) => void;
};

function hostedRootSchematicPath(
  revisionTree: string[] | undefined,
  sources: Array<{ path: string; name?: string }>,
): string | null {
  const tree = revisionTree?.length
    ? revisionTree
    : sources.map((src) => src.path);
  const files = tree.map(normalizeTreePath).filter(Boolean);
  return files.find(isKicadSch) ?? null;
}

/** `project_path` for `set_active_page` (vendor resolves via page_by_path). */
export function kiCanvasProjectPathForTreeFile(
  project: KiCanvasProjectLike,
  filePath: string,
  sources: Array<{ path: string; name?: string }>,
  revisionTree?: string[],
): string | null {
  const urlPath = normalizeTreePath(filePath);
  const rootPath = hostedRootSchematicPath(revisionTree, sources);
  if (!urlPath || !rootPath || urlPath === rootPath) return null;
  const wantBase = basenamePath(urlPath);
  if (typeof project.pages !== "function") return null;
  for (const page of project.pages()) {
    const filePart = page.filename ?? page.name ?? "";
    if (!filePart) continue;
    const head = filePart.split(":")[0] ?? filePart;
    const pageBase = basenamePath(head);
    if (pageBase === wantBase || normalizeTreePath(head) === urlPath) {
      return page.project_path ?? null;
    }
  }
  return null;
}

type KiCanvasEmbedElement = HTMLElement & {
  loaded?: boolean;
  loading?: boolean;
};

const KICANVAS_ATTACH_POLL_MS = 80;
const KICANVAS_ATTACH_POLL_MAX_MS = 15_000;

function kiCanvasEmbedReady(embed: HTMLElement): boolean {
  const el = embed as KiCanvasEmbedElement;
  return el.loaded === true && el.loading !== true;
}

export function readKiCanvasProject(embed: HTMLElement | null | undefined): KiCanvasProjectLike | null {
  if (!embed) return null;
  const rec = embed as HTMLElement & { project?: KiCanvasProjectLike };
  if (rec.project) return rec.project;
  const panel = embed.shadowRoot?.querySelector("kc-project-panel") as
    | (HTMLElement & { project?: KiCanvasProjectLike })
    | null;
  return panel?.project ?? null;
}

export type SubscribeKiCanvasSheetChangeOpts = {
  urlFilePath?: string | null;
  sources?: Array<{ path: string; name?: string }>;
  /** Full revision tree (KiCanvas `sources` may be selected file only). */
  revisionTree?: string[];
};

export function subscribeKiCanvasSheetChange(
  embed: HTMLElement,
  onChange: (page: KiCanvasPageRef) => void,
  opts?: SubscribeKiCanvasSheetChangeOpts,
): () => void {
  let unsub: (() => void) | null = null;
  let cancelled = false;
  let lastKey = "";
  let baselineReady = false;
  let pollAttach: ReturnType<typeof setInterval> | null = null;

  const emit = (page: KiCanvasPageRef) => {
    const key = `${page.filename ?? ""}|${page.project_path ?? ""}`;
    if (!key || key === lastKey) return;
    lastKey = key;
    onChange(page);
  };

  const readActivePage = (): KiCanvasPageRef | null => {
    const project = readKiCanvasProject(embed);
    const page = project?.active_page;
    if (!page) return null;
    return {
      filename: page.filename ?? page.name ?? null,
      project_path: page.project_path ?? null,
    };
  };

  const onProjectChange = () => {
    const page = readActivePage();
    if (!page) return;
    const key = `${page.filename ?? ""}|${page.project_path ?? ""}`;
    if (!baselineReady) {
      baselineReady = true;
      lastKey = key;
      return;
    }
    emit(page);
  };

  const attach = () => {
    if (cancelled || unsub) return;
    if (!kiCanvasEmbedReady(embed)) return;
    const project = readKiCanvasProject(embed);
    if (!project) return;

    const sources = opts?.sources ?? [];
    const urlFile = opts?.urlFilePath?.trim() ?? "";
    // Sync URL → KiCanvas before listening; vendor root snapback already happened pre-ready.
    if (urlFile && typeof project.set_active_page === "function") {
      const projectPath = kiCanvasProjectPathForTreeFile(
        project,
        urlFile,
        sources,
        opts?.revisionTree,
      );
      if (projectPath) project.set_active_page(projectPath);
    }

    const handler = () => onProjectChange();
    if (typeof project.addEventListener === "function") {
      project.addEventListener("change", handler);
      unsub = () => project.removeEventListener?.("change", handler);
    } else if (typeof project.on === "function") {
      project.on("change", handler);
      unsub = () => project.off?.("change", handler);
    } else {
      unsub = () => {};
    }

    const baseline = readActivePage();
    if (baseline) {
      baselineReady = true;
      lastKey = `${baseline.filename ?? ""}|${baseline.project_path ?? ""}`;
    }

    if (pollAttach) {
      clearInterval(pollAttach);
      pollAttach = null;
    }
  };

  const pollStartedAt = Date.now();
  pollAttach = setInterval(() => {
    if (Date.now() - pollStartedAt > KICANVAS_ATTACH_POLL_MAX_MS) {
      if (pollAttach) clearInterval(pollAttach);
      pollAttach = null;
      return;
    }
    attach();
  }, KICANVAS_ATTACH_POLL_MS);
  attach();
  return () => {
    cancelled = true;
    if (pollAttach) clearInterval(pollAttach);
    unsub?.();
  };
}

export function isKicadViewerPath(path: string): boolean {
  return isKicadSch(path) || isKicadPcb(path) || isKicadPro(path);
}

export function normalizeTreePath(path: string): string {
  return path.replaceAll("\\", "/").replace(/^\/+/, "");
}

function decodeTreeToken(path: string): string {
  let current = normalizeTreePath(path);
  for (let i = 0; i < 3; i += 1) {
    try {
      const next = decodeURIComponent(current);
      if (next === current) break;
      current = next;
    } catch {
      break;
    }
  }
  return current;
}

export function treePathSet(tree: string[]): Set<string> {
  return new Set(tree.map(normalizeTreePath).filter(Boolean));
}

export function pathInRevisionTree(path: string, tree: string[]): boolean {
  const want = decodeTreeToken(path);
  return tree.some((entry) => decodeTreeToken(entry) === want);
}

/** Same-stem `.kicad_sch` as the `.kicad_pro`, else first schematic in the tree. */
export function projectRootSchematicPath(
  tree: string[],
  proPath: string,
  projectText?: string,
): string | null {
  const files = tree.map(normalizeTreePath).filter(Boolean);
  if (projectText) {
    try {
      const parsed = JSON.parse(projectText) as { sheets?: unknown };
      const sheets = parsed.sheets;
      const first = Array.isArray(sheets)
        ? Array.isArray(sheets[0])
          ? sheets[0][0]
          : sheets[0]
        : null;
      if (typeof first === "string" && first.trim()) {
        const want = normalizeTreePath(first.trim());
        const hit = files.find(
          (path) => path === want || basenamePath(path) === basenamePath(want),
        );
        if (hit && isKicadSch(hit)) return hit;
      }
    } catch {
      /* sexp .kicad_pro — fall through to stem match */
    }
  }
  const stem = basenamePath(normalizeTreePath(proPath)).replace(/\.kicad_pro$/i, "");
  const same = files.find(
    (path) => isKicadSch(path) && basenamePath(path).replace(/\.kicad_sch$/i, "") === stem,
  );
  return same ?? files.find(isKicadSch) ?? null;
}

/** Same-stem `.kicad_pcb` as the `.kicad_pro`, else first board in the tree. */
export function projectBoardPath(tree: string[], proPath: string): string | null {
  const files = tree.map(normalizeTreePath).filter(Boolean);
  const stem = basenamePath(normalizeTreePath(proPath)).replace(/\.kicad_pro$/i, "");
  const same = files.find(
    (path) => isKicadPcb(path) && basenamePath(path).replace(/\.kicad_pcb$/i, "") === stem,
  );
  return same ?? files.find(isKicadPcb) ?? null;
}

/**
 * KiCanvas sources for the selected file — selected document only.
 *
 * Official embed path (`kicanvas-b031159.js` KiCanvasEmbedElement.#s):
 * `<kicanvas-source src>` → FetchFileSystem; inline children → LocalFileSystem
 * + `load_inline_source` (copies the whole text node on the main thread).
 * `KiCanvasSourceElement.is_inline_source`: `src===null && childNodes.length>0`.
 * Selected documents receive single-flight GET text as those children (no `src`).
 * Sibling sheets stay out of the list so `Project.load` `has()` skips them.
 * Hierarchical names still use `createKiCanvasResolver` (KiCanvas's own GET
 * if FetchFileSystem is constructed; CDP showed that was not among the 6).
 * Upstream issue #79: never list a `.kicad_pcb` next to a schematic.
 */
export function kicadSourcePaths(tree: string[], selectedPath: string, projectText?: string): string[] {
  const selected = normalizeTreePath(selectedPath);
  const files = tree.map(normalizeTreePath).filter(Boolean);
  const present = new Set(files);
  if (!present.has(selected)) return [];
  if (isKicadPcb(selected) || isKicadSch(selected)) return [selected];
  if (isKicadPro(selected)) {
    const root = projectRootSchematicPath(files, selected, projectText);
    const out = [selected];
    if (root && present.has(root)) out.push(root);
    return out;
  }
  return [];
}

export type KicadInspectResult =
  | { ok: true; text: string }
  | { ok: false; reason: string; text: string };

/** Sync validate + board-layer rewrite. Prefer `inspectKicadTextOffThread`. */
export function inspectKicadTextSync(path: string, text: string): KicadInspectResult {
  const kind = documentKindForPath(path);
  const next = kind === "board" ? ensureStandardBoardLayers(text) : text;
  const checked = validateKicadDocumentText(next, path);
  if (!checked.ok) return { ok: false, reason: checked.reason, text: next };
  if (
    kind === "schematic" &&
    (!schematicHasDrawableContent(next) || !schematicSymbolsHaveLibDefs(next))
  ) {
    return { ok: false, reason: "schematic has no drawable content", text: next };
  }
  return { ok: true, text: next };
}

const KICAD_ROOT = {
  schematic: "kicad_sch",
  board: "kicad_pcb",
  project: "kicad_pro",
} as const;

const SCHEMATIC_CONTENT_NODES = new Set(["lib_symbols", "sheet", "symbol"]);
const SCHEMATIC_DRAWABLE_NODES = new Set([
  "sheet",
  "symbol",
  "wire",
  "bus",
  "bus_entry",
  "junction",
  "no_connect",
  "label",
  "global_label",
  "hierarchical_label",
]);
const BOARD_CONTENT_NODES = new Set(["layers", "net", "footprint"]);

/** Scan one top-level s-expression and the names of its immediate children. */
export function parseTopLevelSexp(text: string): { name: string; children: string[] } | null {
  const src = text ?? "";
  const start = src.search(/\(\s*[A-Za-z_][\w-]*/);
  if (start < 0) return null;
  const nameMatch = /^\(\s*([A-Za-z_][\w-]*)/.exec(src.slice(start));
  if (!nameMatch?.[1]) return null;
  const children: string[] = [];
  let i = start + nameMatch[0].length;
  let depth = 1;
  while (i < src.length && depth > 0) {
    const c = src[i]!;
    if (c === '"') {
      i += 1;
      while (i < src.length && src[i] !== '"') {
        if (src[i] === "\\") i += 1;
        i += 1;
      }
      i += 1;
      continue;
    }
    if (c === "(") {
      if (depth === 1) {
        const child = /^\(\s*([A-Za-z_][\w-]*)/.exec(src.slice(i));
        if (child?.[1]) children.push(child[1]);
      }
      depth += 1;
    } else if (c === ")") {
      depth -= 1;
    }
    i += 1;
  }
  if (depth !== 0) return null;
  return { name: nameMatch[1], children };
}

function hasChild(nodes: string[], name: string): boolean {
  return nodes.some((n) => n.toLowerCase() === name);
}

/**
 * KiCad standard 2-layer + technical table (pcbnew default).
 *
 * File format: https://dev-docs.kicad.org/en/file-formats/sexpr-pcb/
 * Layer IDs / names: https://dev-docs.kicad.org/en/file-formats/sexpr-intro/#_layers
 * KiCanvas `Layer` parser: positional ordinal, canonical_name, type, user_name.
 * Board reader: `list("layers", item(Layer))`. LayerSet looks up `F.Cu` /
 * `B.Cu` / `F.Mask` / … by canonical name; an empty `board.layers` leaves
 * only Presets and the drawing-sheet frame.
 *
 * CELUS / some third-party generators omit the top-level `(layers …)` table
 * (names appear only inside footprints). Do not write this back to SoT.
 */
export const KICAD_STANDARD_BOARD_LAYERS = `  (layers
    (0 "F.Cu" signal)
    (31 "B.Cu" signal)
    (32 "B.Adhes" user "B.Adhesive")
    (33 "F.Adhes" user "F.Adhesive")
    (34 "B.Paste" user)
    (35 "F.Paste" user)
    (36 "B.SilkS" user "B.Silkscreen")
    (37 "F.SilkS" user "F.Silkscreen")
    (38 "B.Mask" user)
    (39 "F.Mask" user)
    (40 "Dwgs.User" user "User.Drawings")
    (41 "Cmts.User" user "User.Comments")
    (42 "Eco1.User" user "User.Eco1")
    (43 "Eco2.User" user "User.Eco2")
    (44 "Edge.Cuts" user)
    (45 "Margin" user)
    (46 "B.CrtYd" user "B.Courtyard")
    (47 "F.CrtYd" user "F.Courtyard")
    (48 "B.Fab" user)
    (49 "F.Fab" user)
  )`;

export function boardHasTopLevelLayers(text: string): boolean {
  const parsed = parseTopLevelSexp(text);
  return Boolean(parsed && parsed.name.toLowerCase() === KICAD_ROOT.board && hasChild(parsed.children, "layers"));
}

function skipSexpString(src: string, start: number): number {
  let i = start + 1;
  while (i < src.length && src[i] !== '"') {
    if (src[i] === "\\") i += 1;
    i += 1;
  }
  return i + 1;
}

function insertAfterTopLevelChild(text: string, childName: string, block: string): string | null {
  const start = text.search(/\(\s*kicad_pcb\b/i);
  if (start < 0) return null;
  let i = start;
  let depth = 0;
  let childStart = -1;
  while (i < text.length) {
    const c = text[i]!;
    if (c === '"') {
      i = skipSexpString(text, i);
      continue;
    }
    if (c === "(") {
      depth += 1;
      if (depth === 2) {
        const child = /^\(\s*([A-Za-z_][\w-]*)/.exec(text.slice(i));
        if (child?.[1]?.toLowerCase() === childName) childStart = i;
      }
      i += 1;
      continue;
    }
    if (c === ")") {
      if (depth === 2 && childStart >= 0) {
        const after = i + 1;
        const prefix = text.slice(after).startsWith("\n") ? "" : "\n";
        const suffix = block.endsWith("\n") ? "" : "\n";
        return `${text.slice(0, after)}${prefix}${block}${suffix}${text.slice(after)}`;
      }
      depth -= 1;
      if (depth === 0) break;
    }
    i += 1;
  }
  return null;
}

/** In-memory only — never persist the injected table back to R2 / git. */
export function ensureStandardBoardLayers(text: string): string {
  const body = text ?? "";
  if (!body.trim() || boardHasTopLevelLayers(body)) return body;
  const afterGeneral = insertAfterTopLevelChild(body, "general", KICAD_STANDARD_BOARD_LAYERS);
  if (afterGeneral) return afterGeneral;
  return body.replace(/(\(\s*kicad_pcb\b[^\n]*\n)/i, `$1${KICAD_STANDARD_BOARD_LAYERS}\n`);
}

export function extractSheetfiles(text: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const re = /\(\s*property\s+"(?:Sheetfile|Sheet file)"\s+"([^"]+)"/gi;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text ?? ""))) {
    const value = (match[1] ?? "").trim().replaceAll("\\", "/");
    if (!value || seen.has(value)) continue;
    seen.add(value);
    out.push(value);
  }
  return out;
}

/**
 * KiCanvas LocalFileSystem keys by `kicanvas-source name`. Project.load looks
 * up `dirname(parent.filename) + Sheetfile` via has() — not custom_resolver.
 * Bare filename matches the usual Sheetfile. https://kicanvas.org
 */
export function kicanvasVfsName(path: string): string {
  return basenamePath(normalizeTreePath(path));
}

/** Prefer the Sheetfile string KiCanvas will has() — basename or relative path. */
export function kicanvasSourceName(path: string, sheetfiles: readonly string[] = []): string {
  const norm = normalizeTreePath(path);
  const base = basenamePath(norm);
  const exact = sheetfiles.find((sheet) => normalizeTreePath(sheet) === norm);
  if (exact) return normalizeTreePath(exact);
  const byBase = sheetfiles.find((sheet) => basenamePath(normalizeTreePath(sheet)) === base);
  if (byBase) {
    const value = normalizeTreePath(byBase);
    return value.includes("/") ? value : basenamePath(value);
  }
  return base;
}

export function kicanvasVfsAliases(path: string, sheetfiles: readonly string[] = []): string[] {
  const norm = normalizeTreePath(path);
  const base = basenamePath(norm);
  const names = [base];
  if (norm && norm !== base) names.push(norm);
  for (const sheet of sheetfiles) {
    const value = normalizeTreePath(sheet);
    if (value === base || value === norm || basenamePath(value) === base) {
      if (value && !names.includes(value)) names.push(value);
    }
  }
  return names;
}

/** Instances / wires / sheets — empty `(lib_symbols)` is not drawable. */
export function schematicHasDrawableContent(text: string): boolean {
  const parsed = parseTopLevelSexp(text);
  if (!parsed || parsed.name.toLowerCase() !== KICAD_ROOT.schematic) return false;
  if (parsed.children.some((n) => SCHEMATIC_DRAWABLE_NODES.has(n.toLowerCase()))) return true;
  return extractSheetfiles(text).length > 0;
}

function collectSexpStrings(text: string, root: string, child: string): string[] {
  const src = text ?? "";
  const start = src.search(new RegExp(`\\(\\s*${root}\\b`, "i"));
  if (start < 0) return [];
  const out: string[] = [];
  let i = start;
  let depth = 0;
  while (i < src.length) {
    const c = src[i]!;
    if (c === '"') {
      i += 1;
      while (i < src.length && src[i] !== '"') {
        if (src[i] === "\\") i += 1;
        i += 1;
      }
      i += 1;
      continue;
    }
    if (c === "(") {
      depth += 1;
      if (depth === 2) {
        const match = new RegExp(`^\\(\\s*${child}\\s+"([^"]+)"`, "i").exec(src.slice(i));
        if (match?.[1]) out.push(match[1]);
      }
      i += 1;
      continue;
    }
    if (c === ")") {
      depth -= 1;
      if (depth === 0) break;
    }
    i += 1;
  }
  return out;
}

/** lib_id / lib_name on schematic symbol instances (not lib_symbols children). */
export function extractSymbolLibIds(text: string): string[] {
  const src = text ?? "";
  const libStart = src.search(/\(\s*lib_symbols\b/i);
  let libEnd = -1;
  if (libStart >= 0) {
    let depth = 0;
    for (let i = libStart; i < src.length; i += 1) {
      if (src[i] === '"') {
        i += 1;
        while (i < src.length && src[i] !== '"') {
          if (src[i] === "\\") i += 1;
          i += 1;
        }
        continue;
      }
      if (src[i] === "(") depth += 1;
      else if (src[i] === ")") {
        depth -= 1;
        if (depth === 0) {
          libEnd = i;
          break;
        }
      }
    }
  }
  const body = libEnd >= 0 ? src.slice(0, libStart) + src.slice(libEnd + 1) : src;
  const out: string[] = [];
  const seen = new Set<string>();
  const re = /\(\s*lib_(?:id|name)\s+"([^"]+)"/gi;
  let match: RegExpExecArray | null;
  while ((match = re.exec(body))) {
    const id = (match[1] ?? "").trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

export function extractLibSymbolNames(text: string): string[] {
  return collectSexpStrings(text, "lib_symbols", "symbol");
}

/**
 * KiCanvas SchematicSymbolPainter calls paint_item(lib_symbol). A missing
 * lib_symbols entry is undefined → TypeError reading 'constructor'.
 */
export function schematicSymbolsHaveLibDefs(text: string): boolean {
  const ids = extractSymbolLibIds(text);
  if (ids.length === 0) return true;
  const defs = new Set(extractLibSymbolNames(text));
  return ids.every((id) => defs.has(id));
}

function validateSchematicSexp(
  parsed: { name: string; children: string[] },
  text?: string,
): { ok: true } | { ok: false; reason: string } {
  if (parsed.name.toLowerCase() !== KICAD_ROOT.schematic) {
    return { ok: false, reason: "not a valid schematic document" };
  }
  if (!hasChild(parsed.children, "version")) {
    return { ok: false, reason: "schematic is missing a version node" };
  }
  if (!parsed.children.some((n) => SCHEMATIC_CONTENT_NODES.has(n.toLowerCase()))) {
    return { ok: false, reason: "schematic is missing lib_symbols or sheet content" };
  }
  const body = text ?? "";
  if (body && !schematicHasDrawableContent(body)) {
    return { ok: false, reason: "schematic has no drawable content" };
  }
  if (body && !schematicSymbolsHaveLibDefs(body)) {
    return { ok: false, reason: "schematic has no drawable content" };
  }
  return { ok: true };
}

function validateBoardSexp(parsed: { name: string; children: string[] }): { ok: true } | { ok: false; reason: string } {
  if (parsed.name.toLowerCase() !== KICAD_ROOT.board) {
    return { ok: false, reason: "not a valid board document" };
  }
  if (!hasChild(parsed.children, "version")) {
    return { ok: false, reason: "board is missing a version node" };
  }
  if (!parsed.children.some((n) => BOARD_CONTENT_NODES.has(n.toLowerCase()))) {
    return { ok: false, reason: "board is missing layers, net, or footprint content" };
  }
  return { ok: true };
}

function validateProjectText(text: string): { ok: true } | { ok: false; reason: string } {
  const trimmed = text.trim();
  if (trimmed.startsWith("{")) {
    try {
      const parsed = JSON.parse(trimmed) as { meta?: unknown; version?: unknown };
      if (parsed && typeof parsed === "object" && (parsed.meta != null || parsed.version != null)) {
        return { ok: true };
      }
    } catch {
      return { ok: false, reason: "not a valid project document" };
    }
    return { ok: false, reason: "not a valid project document" };
  }
  const sexp = parseTopLevelSexp(text);
  if (!sexp || sexp.name.toLowerCase() !== KICAD_ROOT.project || !hasChild(sexp.children, "version")) {
    return { ok: false, reason: "not a valid project document" };
  }
  return { ok: true };
}

export function validateKicadDocumentText(
  text: string,
  path: string,
): { ok: true } | { ok: false; reason: string } {
  const kind = documentKindForPath(path);
  const body = text ?? "";
  if (!body.trim()) {
    return { ok: false, reason: "empty document" };
  }
  if (kind === "project") return validateProjectText(body);
  const parsed = parseTopLevelSexp(body);
  if (!parsed) {
    return { ok: false, reason: `not a valid ${kind} document` };
  }
  if (kind === "board") return validateBoardSexp(parsed);
  return validateSchematicSexp(parsed, body);
}

export function documentKindForPath(path: string): "schematic" | "board" | "project" {
  if (isKicadPcb(path)) return "board";
  if (isKicadPro(path)) return "project";
  return "schematic";
}

export function surfaceForPath(path: string): "schematic" | "pcb" | "none" {
  if (isKicadPcb(path)) return "pcb";
  if (isKicadSch(path) || isKicadPro(path)) return "schematic";
  return "none";
}

/** Map a KiCanvas VFS name to the authed file URL, or null when it is not in the revision tree. */
export function resolveKiCanvasSourceName(
  name: string,
  tree: string[],
  org: string,
  proj: string,
  revId: string,
): string | null {
  const raw = name.trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw) || raw.startsWith("/api/")) return raw;
  const stripped = normalizeTreePath(raw);
  const files = tree.map(normalizeTreePath);
  const exact = files.find((path) => path === stripped);
  if (exact) return fileUrl(org, proj, revId, exact);
  const base = basenamePath(stripped);
  const byBase = files.filter((path) => basenamePath(path) === base);
  if (byBase.length === 1) return fileUrl(org, proj, revId, byBase[0]!);
  if (byBase.length > 1) {
    const nested = byBase.find((path) => path.endsWith(`/${stripped}`) || path === stripped);
    if (nested) return fileUrl(org, proj, revId, nested);
  }
  return null;
}

export class MissingKiCanvasSourceError extends Error {
  readonly sourceName: string;
  constructor(sourceName: string) {
    super(`KiCanvas source is not in this revision: ${sourceName}`);
    this.name = "MissingKiCanvasSourceError";
    this.sourceName = sourceName;
  }
}

export function createKiCanvasResolver(
  org: string,
  proj: string,
  revId: string,
  tree: string[],
  onMissing?: (name: string) => void,
): (name: string) => URL {
  return (name: string) => {
    const href = resolveKiCanvasSourceName(name, tree, org, proj, revId);
    if (!href) {
      onMissing?.(name);
      throw new MissingKiCanvasSourceError(name);
    }
    if (/^https?:\/\//i.test(href)) return new URL(href);
    const origin = typeof window !== "undefined" && window.location?.origin ? window.location.origin : "https://pcbx.ai";
    return new URL(href, origin);
  };
}

export type KiCanvasSourceIssue = {
  path: string;
  kind: "missing" | "invalid";
  message: string;
};

export function collectKiCanvasSourceIssues(
  candidates: string[],
  existing: Set<string>,
  invalid: Map<string, string>,
): KiCanvasSourceIssue[] {
  const issues: KiCanvasSourceIssue[] = [];
  for (const path of candidates) {
    const reason = invalid.get(path);
    if (reason) {
      const missing = /^HTTP 404\b/.test(reason);
      issues.push({
        path,
        kind: missing ? "missing" : "invalid",
        message: missing ? `Unable to load ${path} (not in this revision).` : `${path}: ${reason}`,
      });
      continue;
    }
    if (!existing.has(path)) {
      issues.push({ path, kind: "missing", message: `Unable to load ${path} (not in this revision).` });
    }
  }
  return issues;
}

export function hasWebGL2(doc: Document = document): boolean {
  try {
    const canvas = doc.createElement("canvas");
    return Boolean(canvas.getContext("webgl2"));
  } catch {
    return false;
  }
}

export const KICANVAS_EMPTY_CONTENT =
  "This schematic has no drawable content.";

/** WebGL renderer disposed while a frame is still queued (clear_canvas / on_draw). */
export function isKiCanvasDisposedError(reason: string): boolean {
  return /uninitialized|disposed|setTransform/i.test(reason);
}

export function isKiCanvasRuntimeError(reason: string): boolean {
  if (isKiCanvasDisposedError(reason)) return false;
  return /constructor|painter_for|visible|Unable to find|No vaild root|no drawable content/i.test(
    reason,
  );
}

export function kicanvasEmptyStateMessage(
  reason: string,
  kind: "schematic" | "board" | "project" = "schematic",
): string {
  const raw = reason.trim();
  if (isKiCanvasDisposedError(raw)) return raw;
  if (kind !== "schematic") return raw;
  if (
    isKiCanvasRuntimeError(raw) ||
    /not a valid schematic|missing lib_symbols|empty document/i.test(raw)
  ) {
    return KICANVAS_EMPTY_CONTENT;
  }
  return raw;
}

export function isStaleRevisionLoad(requestedRev: string, currentRev: string): boolean {
  return requestedRev !== currentRev;
}
